import { API_CONFIG } from '../config';
import { messageFromFailedResponse } from '../utils/apiErrorMessage';
import { getParachuteAuthToken } from '../utils/parachuteAuthToken';

/** Never use the browser HTTP cache for these APIs — always load from the backend. */
const NO_STORE: RequestInit = { cache: 'no-store' };

type FetchMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function authenticatedFetch(
  url: string,
  options: { method?: FetchMethod; body?: unknown; requireAuth?: boolean; signal?: AbortSignal } = {}
): Promise<Response> {
  const { method = 'GET', body, requireAuth = false, signal } = options;
  const token = getParachuteAuthToken();
  if (requireAuth && !token) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  return fetch(url, {
    ...NO_STORE,
    method,
    headers,
    ...(signal ? { signal } : {}),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

async function fetchJson<T>(
  url: string,
  options: {
    method?: FetchMethod;
    body?: unknown;
    requireAuth?: boolean;
    errorMessage?: string;
    signal?: AbortSignal;
  } = {}
): Promise<T> {
  const { errorMessage = 'Request failed', ...fetchOpts } = options;
  const response = await authenticatedFetch(url, fetchOpts);
  if (response.status === 404) throw new Error('Not found');
  if (!response.ok) throw new Error(await messageFromFailedResponse(response, errorMessage));
  const json = await response.json();
  return json;
}

async function fetchJsonData<T>(
  url: string,
  options: {
    method?: FetchMethod;
    body?: unknown;
    requireAuth?: boolean;
    errorMessage?: string;
    signal?: AbortSignal;
  } = {}
): Promise<T> {
  const json = await fetchJson<{ data?: T }>(url, options);
  return (json as any)?.data ?? json;
}

async function fetchJsonDataArray<T>(
  url: string,
  options: {
    method?: FetchMethod;
    body?: unknown;
    requireAuth?: boolean;
    errorMessage?: string;
    signal?: AbortSignal;
  } = {}
): Promise<T[]> {
  const json = await fetchJson<{ data?: T[] }>(url, options);
  return Array.isArray((json as any)?.data) ? (json as any).data : (json as any);
}

async function fetchJsonNullable<T>(
  url: string,
  options: {
    method?: FetchMethod;
    body?: unknown;
    requireAuth?: boolean;
    errorMessage?: string;
    signal?: AbortSignal;
  } = {}
): Promise<T | null> {
  const { errorMessage = 'Request failed', ...fetchOpts } = options;
  const response = await authenticatedFetch(url, fetchOpts);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await messageFromFailedResponse(response, errorMessage));
  const json = await response.json();
  return (json as any)?.data ?? json;
}

/**
 * Analysis data model – aligned with client sidebar menu sections.
 * Each group of fields maps to one sidebar item (or Parachute sub-item).
 */
/** Disqualified individuals row — `id` is stable client key for compensation maps. */
export interface AnalysisIndividualRow {
  id: string;
  name: string;
  /** Contact email — stored on the shared `AnalysisContacts` row; powers DQI waiver agreements. */
  email?: string;
  title: string;
  category: 'hci' | 'officer' | 'shareholder';
  /** Optional UI flag; not sent to or returned from API. */
  isHci?: boolean;
}

/** One row in the Severance sub-step; keys match API `PUT …/severance` item body (camelCase). */
export interface AnalysisSeveranceRow {
  salaryAmount?: string;
  salaryUnit?: string;
  bonusPortion?: string;
  cobraMonths?: string;
  other?: string;
  paymentTiming?: string;
}

export interface AnalysisData {
  // ─── Sidebar: Transaction ─────────────────────────────────────────────
  companyName: string;
  closingDate: string;
  dealPrice: string | number;
  afr: string;
  /** Highest employee headcount (12-month period prior to closing). */
  headcount: string;

  // ─── Sidebar: Individuals ───────────────────────────────────────────
  individuals: AnalysisIndividualRow[];

  // ─── Sidebar: Compensation ───────────────────────────────────────────
  compensation: Record<string, unknown>;

  // ─── Sidebar: Base Amount ────────────────────────────────────────────
  baseAmountData: Record<string, unknown>;

  // ─── Sidebar: Parachute Payments (sub-items) ──────────────────────────
  /** Per `individuals[].id`: severance grid — persisted via PUT …/severance (`AnalysisSeverances`). */
  severanceData: Record<string, AnalysisSeveranceRow>;
  cicBonusData: Record<string, unknown>; // CIC Bonuses
  compIncreaseData: Record<string, unknown>; // Comp Increases
  equityData: Record<string, unknown>; // Equity Awards
  buyerEquityData: Record<string, unknown>; // Buyer Equity Awards
  otherPaymentsData: Record<string, unknown>; // Other Payments

  // ─── Sidebar: 280G Results (read-only / computed from above) ───────────
  // No separate storage; component derives from transaction + individuals + comp + parachute.

  // ─── Sidebar: Shareholder Approval ───────────────────────────────────
  // No separate storage; component uses form data for document generation.

  /** Mirrored from linked `Companies` row when `companyId` is set (API read-only). */
  companyWorkStatus?: string;
  dealStructure?: string;
  dealRole?: string;
  listingStatus?: string;
}

/** Path segment for `PUT …/analyses/{id}/parachute/{section}` (JSON substeps; severance uses `/severance`). */
export const PARACHUTE_JSON_SECTION_SLUGS = {
  cicBonus: 'cic-bonus',
  compIncreases: 'comp-increases',
  equity: 'equity',
  buyerEquity: 'buyer-equity',
  otherPayments: 'other-payments',
} as const;

export type ParachuteJsonSectionKey = keyof typeof PARACHUTE_JSON_SECTION_SLUGS;

/** `AnalysisData` key for each JSON parachute sub-step (matches SPA state). */
export const PARACHUTE_JSON_SECTION_DATA_KEY: Record<ParachuteJsonSectionKey, keyof AnalysisData> = {
  cicBonus: 'cicBonusData',
  compIncreases: 'compIncreaseData',
  equity: 'equityData',
  buyerEquity: 'buyerEquityData',
  otherPayments: 'otherPaymentsData',
};

/** Same order as workflow sidebar Parachute sub-items after Severance. */
export const PARACHUTE_JSON_SECTION_ORDER: readonly ParachuteJsonSectionKey[] = [
  'cicBonus',
  'compIncreases',
  'equity',
  'buyerEquity',
  'otherPayments',
];

/** Sidebar menu sections and their AnalysisData keys (for progress/validation) */
export const SIDEBAR_DATA_KEYS: Record<string, (keyof AnalysisData)[]> = {
  transaction: ['companyName', 'closingDate', 'dealPrice', 'afr', 'headcount'],
  individuals: ['individuals'],
  compensation: ['compensation'],
  baseAmount: ['baseAmountData'],
  /** Order matches client `parachuteWorkflowSubItems` (`PARACHUTE_WORKFLOW_SUB_ITEMS` / `PARACHUTE_SUB_ID_TO_DATA_KEY`). */
  parachutePayments: [
    'severanceData',
    'cicBonusData',
    'compIncreaseData',
    'equityData',
    'buyerEquityData',
    'otherPaymentsData',
  ],
  analysisResults: [], // derived
  shareholderApproval: [], // documents derived from form data
};

/** GET /analyses list: five main steps vs. client sidebar (transaction → parachute). */
export interface AnalysisWorkflowProgress {
  individualsCount: number;
  stepsCompleted: number;
  totalSteps: number;
  percent: number;
  /** not_started | in_progress | complete */
  status: string;
}

export interface Analysis {
  id: string;
  name: string;
  /** `editable` (default) or `final` — final analyses are read-only on the server. */
  status?: string;
  /** Best-effort creator identifier (derived from earliest AnalysisRuns row). */
  createdBy?: string;
  /** Analysis creation date (server). */
  createdOn?: string;
  /** ISO 8601 UTC when workflow data last changed; absent/null if never saved. */
  workflowLastChangedAt?: string | null;
  /** ISO 8601 UTC when a 280G run was last recorded; absent/null if never run. */
  lastRunAt?: string | null;
  /** ISO 8601 UTC when Vote Room was launched; null before launch. */
  voteRoomLaunchedAt?: string | null;
  /** Frozen Vote Room shutdown settings captured at launch. */
  voteRoomShutdownMode?: 'threshold_75' | 'threshold_custom' | 'time_based' | string | null;
  voteRoomShutdownCustomThreshold?: number | null;
  voteRoomShutdownHours?: number | null;
  /** Most-recent `AnalysisRun.Id` for this analysis; populated after a fresh run or by `getLastAnalysisRun`. */
  lastRunId?: string | null;
  /** Present when this analysis is linked to a firm company row (`Companies` table). */
  lawFirmId?: string;
  companyId?: string;
  /** Law firm display name when linked to a company (from `LawFirms.FirmName`). */
  lawFirmName?: string;
  /** Populated on list API; preserved when hydrating full analysis from GET …/analyses/:id. */
  workflowProgress?: AnalysisWorkflowProgress;
  /** Set when the analysis is soft-deleted (archives list). */
  deleteReason?: string | null;
  data: AnalysisData;
}

export interface LaunchVoteRoomPayload {
  shutdownMode: 'threshold_75' | 'threshold_custom' | 'time_based';
  shutdownCustomThreshold?: number | null;
  shutdownHours?: number | null;
}

export interface VoteRoomShutdownPayload {
  shutdownMode: 'threshold_75' | 'threshold_custom' | 'time_based';
  shutdownCustomThreshold?: number | null;
  shutdownHours?: number | null;
}

export interface VoteRoomPrelaunchEmailVerificationResult {
  analysisId: string;
  eligibleCount: number;
  queuedCount: number;
  failedCount: number;
  invalidEmailCount: number;
  skippedConfirmedCount: number;
  dryRun: boolean;
}

export interface VoteRoomDocumentSendItem {
  documentId?: string | null;
  kind: string;
  status: 'Sent' | 'Skipped' | 'Failed' | 'NotFound' | 'NotRequired' | string;
  envelopeId?: string | null;
  message?: string | null;
  error?: string | null;
}

export interface VoteRoomLaunchSendResult {
  items: VoteRoomDocumentSendItem[];
}

export interface LaunchVoteRoomResponse {
  analysis: Analysis | null;
  voteRoomLaunchSend?: VoteRoomLaunchSendResult | null;
}

export interface WaiverLaunchReadinessItem {
  individualId: string;
  name: string | null;
  status: string;
}

export interface WaiverLaunchReadiness {
  required: boolean;
  ready: boolean;
  items: WaiverLaunchReadinessItem[];
}

/** Linked company for the client user (`GET /api/pct/v1/workspace/company`). */
export interface ClientWorkspaceCompany {
  companyId: string;
  lawFirmId: string;
  lawFirmName: string;
  companyName: string;
  closingDate: string;
  workStatus: string;
  dealStructure?: string | null;
  dealRole?: string | null;
  listingStatus?: string | null;
}

/**
 * List endpoint: metadata + transaction fields only; workflow steps are empty placeholders.
 * Load full step data with {@link getAnalysis} (GET …/analyses/:id).
 */
async function getAnalysesFromBackend(): Promise<Analysis[]> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYSES}`;
  return fetchJsonDataArray<Analysis>(url, { errorMessage: 'Failed to load analyses' });
}

/** Server-side list pagination (`GET …/analyses?page=&pageSize=`). Default SPA path uses {@link getAnalyses} (full list). */
export type AnalysesPageMeta = { total: number; page: number; pageSize: number };

export async function getAnalysesPage(
  page: number,
  pageSize: number,
  signal?: AbortSignal
): Promise<{ analyses: Analysis[]; meta: AnalysesPageMeta }> {
  const p = Math.max(1, page);
  const ps = Math.min(100, Math.max(1, pageSize));
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYSES}?page=${p}&pageSize=${ps}`;
  const json = await fetchJson<{
    data: Analysis[];
    total: number;
    page: number;
    pageSize: number;
  }>(url, {
    requireAuth: true,
    errorMessage: 'Failed to load analyses',
    signal,
  });
  const data = Array.isArray((json as any)?.data) ? (json as any).data : [];
  const total = typeof (json as any)?.total === 'number' ? (json as any).total : data.length;
  const pageOut = typeof (json as any)?.page === 'number' ? (json as any).page : p;
  const pageSizeOut = typeof (json as any)?.pageSize === 'number' ? (json as any).pageSize : ps;
  return { analyses: data as Analysis[], meta: { total, page: pageOut, pageSize: pageSizeOut } };
}

async function getAnalysisFromBackend(id: string): Promise<Analysis> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysis(id)}`;
  return fetchJsonData<Analysis>(url, { errorMessage: 'Failed to load analysis' });
}

async function getArchivedAnalysesFromBackend(): Promise<Analysis[]> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYSES_DELETED}`;
  return fetchJsonDataArray<Analysis>(url, { errorMessage: 'Failed to load archived analyses' });
}

/**
 * Create a new analysis via backend.
 * When `companyLink` is set, sends lawFirmId/companyId so the analysis is tied to that company row.
 */
async function createAnalysisInBackend(
  name: string,
  companyLink?: { lawFirmId: string; companyId: string }
): Promise<Analysis> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYSES}`;
  const body: Record<string, unknown> = { name };
  if (companyLink?.lawFirmId && companyLink?.companyId) {
    body.lawFirmId = companyLink.lawFirmId;
    body.companyId = companyLink.companyId;
  }
  return fetchJsonData<Analysis>(url, { method: 'POST', body, errorMessage: 'Failed to create analysis' });
}

/** Which client workflow steps to duplicate when copying an analysis (API camelCase: transaction, individuals, …). */
export interface CopyAnalysisStepOptions {
  transaction: boolean;
  individuals: boolean;
  compensation: boolean;
  baseAmount: boolean;
  parachute: boolean;
}

export const DEFAULT_COPY_ANALYSIS_STEPS: CopyAnalysisStepOptions = {
  transaction: true,
  individuals: true,
  compensation: true,
  baseAmount: true,
  parachute: true,
};

/**
 * Duplicate an analysis (same company link; copies selected workflow data). Run history is not copied.
 */
async function copyAnalysisInBackend(
  sourceId: string,
  name?: string,
  stepOptions?: CopyAnalysisStepOptions
): Promise<Analysis> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisCopy(sourceId)}`;
  const body: Record<string, unknown> = {};
  const trimmed = (name || '').trim();
  if (trimmed) body.name = trimmed;
  if (stepOptions) {
    body.options = {
      transaction: stepOptions.transaction,
      individuals: stepOptions.individuals,
      compensation: stepOptions.compensation,
      baseAmount: stepOptions.baseAmount,
      parachute: stepOptions.parachute,
    };
  }
  return fetchJsonData<Analysis>(url, { method: 'POST', body, errorMessage: 'Failed to copy analysis' });
}

export type UpdateAnalysisPayload = { name?: string; status?: string };

/**
 * Update analysis metadata (`PUT /analyses/{id}`): name and/or lock status (`final`).
 */
async function updateAnalysisInBackend(id: string, payload: string | UpdateAnalysisPayload): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysis(id)}`;
  const body = typeof payload === 'string' ? { name: payload } : { ...payload };
  return fetchJsonNullable<Analysis>(url, { method: 'PUT', body, errorMessage: 'Failed to update analysis' });
}

type TransactionBasicsPayload = Pick<AnalysisData, 'companyName' | 'closingDate' | 'dealPrice' | 'afr' | 'headcount'>;

/**
 * Replace disqualified individuals for an analysis (`AnalysisIndividuals` table).
 */
async function updateAnalysisIndividualsInBackend(
  id: string,
  individuals: AnalysisIndividualRow[],
  headcount: string
): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisIndividuals(id)}`;
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: {
      headcount: headcount ?? '',
      individuals: individuals.map((i) => ({
        id: String(i.id),
        name: i.name ?? '',
        email: i.email ?? '',
        title: i.title ?? '',
        category: i.category ?? 'hci',
      })),
    },
    errorMessage: 'Failed to update analysis individuals',
  });
}

type CompensationRowPayload = {
  id: string;
  startDate: string;
  baseSalary: string;
  bonusPct: string;
  healthPremium: string;
};

/**
 * Replace compensation rows for an analysis (`AnalysisCompensations` table).
 */
async function updateAnalysisCompensationsInBackend(
  id: string,
  individuals: AnalysisIndividualRow[],
  compensation: Record<string, unknown>
): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisCompensation(id)}`;
  const compensations: CompensationRowPayload[] = individuals.map((i) => {
    const c = (compensation[i.id] as Record<string, unknown> | undefined) || {};
    return {
      id: String(i.id),
      startDate: String(c.startDate ?? ''),
      baseSalary: String(c.baseSalary ?? ''),
      bonusPct: String(c.bonusPct ?? ''),
      healthPremium: String(c.healthPremium ?? ''),
    };
  });
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: { compensations },
    errorMessage: 'Failed to update analysis compensation',
  });
}

type BaseAmountRowPayload = {
  id: string;
  fields: Record<string, unknown>;
};

/**
 * Replace base amount rows for an analysis (`AnalysisBaseAmounts` table).
 */
type SeveranceRowPayload = {
  id: string;
  salaryAmount: string;
  salaryUnit: string;
  bonusPortion: string;
  cobraMonths: string;
  other: string;
  paymentTiming: string;
};

/**
 * Replace severance rows for an analysis (`AnalysisSeverances` table).
 */
async function updateAnalysisSeverancesInBackend(
  id: string,
  individuals: AnalysisIndividualRow[],
  severanceData: Record<string, AnalysisSeveranceRow | unknown>
): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisSeverance(id)}`;
  const severances: SeveranceRowPayload[] = individuals.map((i) => {
    const s = (severanceData[i.id] as Record<string, unknown> | undefined) || {};
    return {
      id: String(i.id),
      salaryAmount: String(s.salaryAmount ?? ''),
      salaryUnit: String(s.salaryUnit ?? 'months'),
      bonusPortion: String(s.bonusPortion ?? 'none'),
      cobraMonths: String(s.cobraMonths ?? ''),
      other: String(s.other ?? ''),
      paymentTiming: String(s.paymentTiming ?? 'lumpAtClose'),
    };
  });
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: { severances },
    errorMessage: 'Failed to update analysis severance data',
  });
}

function fieldsForParachuteJsonRow(sectionSlug: string, raw: unknown): unknown {
  if (sectionSlug === 'cic-bonus') {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...(raw as Record<string, unknown>) };
  }
  return {};
}

/**
 * Replace rows for one Parachute Payments JSON sub-step (`AnalysisParachutePayloads`).
 */
async function updateAnalysisParachuteJsonSectionInBackend(
  id: string,
  sectionSlug: string,
  individuals: AnalysisIndividualRow[],
  record: Record<string, unknown>
): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisParachuteSection(id, sectionSlug)}`;
  const rows = individuals.map((i) => ({
    id: String(i.id),
    fields: fieldsForParachuteJsonRow(sectionSlug, record[i.id]),
  }));
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: { rows },
    errorMessage: 'Failed to update analysis parachute section',
  });
}

async function updateAnalysisBaseAmountsInBackend(
  id: string,
  individuals: AnalysisIndividualRow[],
  baseAmountData: Record<string, unknown>
): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisBaseAmount(id)}`;
  const baseAmounts: BaseAmountRowPayload[] = individuals.map((i) => {
    const row = (baseAmountData[i.id] as Record<string, unknown> | undefined) || {};
    return {
      id: String(i.id),
      fields: { ...row },
    };
  });
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: { baseAmounts },
    errorMessage: 'Failed to update analysis base amount data',
  });
}

/**
 * Update transaction/basic fields stored in the `Analyses` table.
 * Note: backend persists company/closing/deal price/AFR/headcount on this endpoint.
 */
async function updateAnalysisDataInBackend(id: string, payload: TransactionBasicsPayload): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisTransaction(id)}`;
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: payload,
    errorMessage: 'Failed to update analysis data',
  });
}

/**
 * Soft-delete an analysis via backend.
 */
async function deleteAnalysisInBackend(id: string, reason: string): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysis(id)}`;
  const response = await authenticatedFetch(url, { method: 'DELETE', body: { reason } });
  if (response.status === 404) return;
  if (!response.ok) throw new Error('Failed to delete analysis');
}

async function restoreAnalysisInBackend(id: string): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRestore(id)}`;
  const response = await authenticatedFetch(url, { method: 'POST' });
  if (response.status === 404) return;
  if (!response.ok) throw new Error('Failed to restore analysis');
}

export type RecordAnalysisRunPayload = {
  status: 'ok' | 'over_threshold';
  individualsCount: number;
  overCount: number;
  /** Workflow inputs + computed 280G rows; stored in `AnalysisRunSnapshots` on the server. */
  snapshot?: Record<string, unknown>;
};

export type AnalysisLastRun = {
  runId: string;
  runDate: string;
  status: string;
  individualsCount: number;
  overCount: number;
  ranByUserId: number;
  ranByEmail: string;
  ranByName: string;
};

export type AnalysisRunHistoryRow = {
  /** Present for runs recorded after this feature; used for snapshot navigation. */
  runId?: string;
  runDate: string;
  status: string;
  analysisName: string;
  individualsCount: number;
  overCount: number;
  ranByUserId: number;
  ranByEmail: string;
  ranByName: string;
  hasSnapshot?: boolean;
};

async function recordAnalysisRunInBackend(
  id: string,
  payload: RecordAnalysisRunPayload
): Promise<{ runId: string | null }> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRuns(id)}`;
  const response = await authenticatedFetch(url, { method: 'POST', body: payload, requireAuth: true });
  if (!response.ok) {
    let message = 'Failed to record analysis run';
    try {
      const json = await response.json();
      const apiMessage = typeof json?.message === 'string' ? json.message.trim() : '';
      if (apiMessage) {
        message = apiMessage;
      } else {
        message = `${message} (${response.status})`;
      }
    } catch {
      message = `${message} (${response.status})`;
    }
    throw new Error(message);
  }
  try {
    const json = await response.json();
    const runId = json?.data?.runId;
    return { runId: typeof runId === 'string' && runId.length > 0 ? runId : null };
  } catch {
    return { runId: null };
  }
}

async function getLastAnalysisRunInBackend(id: string): Promise<AnalysisLastRun | null> {
  const token = getParachuteAuthToken();
  if (!token) return null;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunsLast(id)}`;
  return fetchJsonNullable<AnalysisLastRun>(url, {
    requireAuth: true,
    errorMessage: 'Failed to load last analysis run',
  });
}

async function getAnalysisRunHistoryInBackend(id: string, limit = 50): Promise<AnalysisRunHistoryRow[]> {
  const token = getParachuteAuthToken();
  if (!token) return [];
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunsHistory(id, limit)}`;
  return fetchJsonDataArray<AnalysisRunHistoryRow>(url, {
    requireAuth: true,
    errorMessage: 'Failed to load analysis run history',
  });
}

/** Stored JSON from GET …/analyses/{id}/runs/{runId}/snapshot (workflow + analysis result rows). */
export type AnalysisRunSnapshotBody = {
  version?: number;
  capturedAtISO?: string;
  workflowData?: AnalysisData;
  analysisResults?: unknown[];
};

async function getAnalysisRunSnapshotFromBackend(
  analysisId: string,
  runId: string
): Promise<AnalysisRunSnapshotBody | null> {
  const token = getParachuteAuthToken();
  if (!token) return null;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunSnapshot(analysisId, runId)}`;
  const json = await fetchJsonNullable<{ snapshot?: unknown }>(url, {
    requireAuth: true,
    errorMessage: 'Failed to load run snapshot',
  });
  const snap = (json as any)?.snapshot ?? (json as any)?.data?.snapshot;
  if (snap == null || typeof snap !== 'object' || Array.isArray(snap)) return null;
  return snap as AnalysisRunSnapshotBody;
}

/** Client portal: linked company row for the logged-in client (deal context on analyses page). */
export async function getClientWorkspaceCompany(signal?: AbortSignal): Promise<ClientWorkspaceCompany | null> {
  const token = getParachuteAuthToken();
  if (!token) return null;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_COMPANY}`;
  const response = await authenticatedFetch(url, { requireAuth: true, signal });
  if (response.status === 404 || response.status === 401 || response.status === 403) return null;
  if (!response.ok) throw new Error('Failed to load workspace company');
  const json = await response.json();
  return json?.data ?? null;
}

/** Client portal: update linked company closing date (`PATCH /api/pct/v1/workspace/company`). */
export async function patchClientWorkspaceCompany(patch: { closingDate: string }): Promise<ClientWorkspaceCompany> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_COMPANY}`;
  return fetchJsonData<ClientWorkspaceCompany>(url, {
    method: 'PATCH',
    body: patch,
    requireAuth: true,
    errorMessage: 'Failed to update closing date',
  });
}

/** List analyses (summary rows; use {@link getAnalysis} for full workflow). */
export async function getAnalyses(): Promise<Analysis[]> {
  return getAnalysesFromBackend();
}

/** Full workflow for one analysis (all steps). */
export async function getAnalysis(id: string): Promise<Analysis> {
  return getAnalysisFromBackend(id);
}

export async function getArchivedAnalyses(): Promise<Analysis[]> {
  return getArchivedAnalysesFromBackend();
}

/** Public API: create a new analysis (now using real API) */
export async function createAnalysis(
  name: string,
  companyLink?: { lawFirmId: string; companyId: string }
): Promise<Analysis> {
  return createAnalysisInBackend(name, companyLink);
}

/** Public API: duplicate an analysis (deep copy of selected steps; same company link). */
export async function copyAnalysis(
  sourceId: string,
  name?: string,
  stepOptions?: CopyAnalysisStepOptions
): Promise<Analysis> {
  return copyAnalysisInBackend(sourceId, name, stepOptions);
}

/** Public API: update analysis name and/or set status to `final` (lock). */
export async function updateAnalysis(id: string, payload: string | UpdateAnalysisPayload): Promise<Analysis | null> {
  return updateAnalysisInBackend(id, payload);
}

/** Public API: update transaction/basic fields stored in the analyses table */
export async function updateAnalysisData(id: string, payload: TransactionBasicsPayload): Promise<Analysis | null> {
  return updateAnalysisDataInBackend(id, payload);
}

/** Public API: persist disqualified individuals (`AnalysisIndividuals`) */
export async function updateAnalysisIndividuals(
  id: string,
  individuals: AnalysisIndividualRow[],
  headcount: string
): Promise<Analysis | null> {
  return updateAnalysisIndividualsInBackend(id, individuals, headcount);
}

/** Public API: persist per-individual compensation (`AnalysisCompensations`) */
export async function updateAnalysisCompensations(
  id: string,
  individuals: AnalysisIndividualRow[],
  compensation: Record<string, unknown>
): Promise<Analysis | null> {
  return updateAnalysisCompensationsInBackend(id, individuals, compensation);
}

/** Public API: persist per-individual base amount / W-2 data (`AnalysisBaseAmounts`) */
export async function updateAnalysisBaseAmounts(
  id: string,
  individuals: AnalysisIndividualRow[],
  baseAmountData: Record<string, unknown>
): Promise<Analysis | null> {
  return updateAnalysisBaseAmountsInBackend(id, individuals, baseAmountData);
}

/** Public API: persist per-individual severance (`AnalysisSeverances`) */
export async function updateAnalysisSeverances(
  id: string,
  individuals: AnalysisIndividualRow[],
  severanceData: Record<string, AnalysisSeveranceRow | unknown>
): Promise<Analysis | null> {
  return updateAnalysisSeverancesInBackend(id, individuals, severanceData);
}

/** Public API: persist one Parachute JSON sub-step (CIC, comp increases, equity, buyer equity, other payments). */
export async function updateAnalysisParachuteJsonSection(
  id: string,
  section: ParachuteJsonSectionKey,
  individuals: AnalysisIndividualRow[],
  record: Record<string, unknown>
): Promise<Analysis | null> {
  const slug = PARACHUTE_JSON_SECTION_SLUGS[section];
  return updateAnalysisParachuteJsonSectionInBackend(id, slug, individuals, record);
}

/**
 * Mark the Vote Room as launched for this analysis (idempotent on the server).
 * Once launched, the per-analysis cap table is locked from edits and shareholder
 * disclosure/consent documents are sent for signature.
 */
export async function launchAnalysisVoteRoom(
  id: string,
  payload: LaunchVoteRoomPayload
): Promise<LaunchVoteRoomResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisVoteRoomLaunch(id)}`;
  try {
    const json = await fetchJson<{
      data?: Analysis;
      voteRoomLaunchSend?: VoteRoomLaunchSendResult | null;
    }>(url, {
      method: 'POST',
      body: payload,
      errorMessage: 'Failed to launch Vote Room',
    });
    return {
      analysis: json?.data ?? null,
      voteRoomLaunchSend: json?.voteRoomLaunchSend ?? null,
    };
  } catch (err) {
    if (err instanceof Error && err.message === 'Not found') {
      return { analysis: null, voteRoomLaunchSend: null };
    }
    throw err;
  }
}

/** Save Vote Room shutdown settings before launch. */
export async function updateAnalysisVoteRoomShutdown(
  id: string,
  payload: VoteRoomShutdownPayload
): Promise<Analysis | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisVoteRoomShutdown(id)}`;
  return fetchJsonNullable<Analysis>(url, {
    method: 'PUT',
    body: payload,
    errorMessage: 'Failed to save Vote Room shutdown settings',
  });
}

export async function getVoteRoomWaiverReadiness(analysisId: string): Promise<WaiverLaunchReadiness | null> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisVoteRoomWaiverReadiness(analysisId)}`;
  return fetchJsonNullable<WaiverLaunchReadiness>(url, {
    errorMessage: 'Failed to load waiver readiness',
  });
}

/** Queue pre-launch verification emails for eligible shareholders before Vote Room launch. */
export async function queueVoteRoomPrelaunchEmailVerification(
  analysisId: string,
  options: { dryRun?: boolean } = {}
): Promise<VoteRoomPrelaunchEmailVerificationResult> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisVoteRoomPrelaunchEmailVerification(analysisId)}`;
  const result = await fetchJsonNullable<VoteRoomPrelaunchEmailVerificationResult>(url, {
    method: 'POST',
    body: { dryRun: options.dryRun ?? false },
    errorMessage: 'Failed to queue pre-launch email verification',
  });
  if (!result) throw new Error('Pre-launch email verification response missing data.');
  return result;
}

/** Public API: soft-delete an analysis (archives it server-side) */
export async function deleteAnalysis(id: string, reason: string): Promise<void> {
  return deleteAnalysisInBackend(id, reason);
}

export async function restoreAnalysis(id: string): Promise<void> {
  return restoreAnalysisInBackend(id);
}

export async function recordAnalysisRun(
  id: string,
  payload: RecordAnalysisRunPayload
): Promise<{ runId: string | null }> {
  return recordAnalysisRunInBackend(id, payload);
}

/**
 * Generated document row — see `AnalysisDocumentDto` on the API. Enum-string fields use Title Case
 * (e.g. `Analysis Summary`, `PDF`, `Ready`) per `.cursor/rules/enum-string-title-case.mdc`.
 */
export interface AnalysisDocumentRow {
  id: string;
  analysisId: string;
  analysisRunId: string;
  kind: 'Analysis Summary' | 'Disclosure Statement' | 'Waiver Agreement' | 'Shareholder Consent' | string;
  format: 'PDF' | 'DOCX' | string;
  variant: string;
  title: string;
  status: 'Generating' | 'Ready' | 'Failed' | 'Superseded' | string;
  version: number;
  byteSize: number | null;
  mimeType: string | null;
  generatedAt: string;
  failureReason: string | null;
  downloadUrl: string | null;
}

export const DOCUMENT_KINDS = {
  AnalysisSummary: 'Analysis Summary',
  DisclosureStatement: 'Disclosure Statement',
  WaiverAgreement: 'Waiver Agreement',
  ShareholderConsent: 'Shareholder Consent',
} as const;

export const DOCUMENT_FORMATS = { Pdf: 'PDF', Docx: 'DOCX' } as const;

/** List documents for an analysis, optionally filtered by run; suppresses `Superseded` by default. */
export async function getAnalysisDocuments(
  analysisId: string,
  options: { runId?: string; latestOnly?: boolean } = {}
): Promise<AnalysisDocumentRow[]> {
  const { runId, latestOnly = true } = options;
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisDocuments(analysisId, runId, latestOnly)}`;
  return fetchJsonDataArray<AnalysisDocumentRow>(url, {
    requireAuth: true,
    errorMessage: 'Failed to load documents',
  });
}

/** Generate one document for a run; idempotent — returns the existing Ready row when present. */
export async function generateAnalysisDocument(
  analysisId: string,
  runId: string,
  body: { kind: string; format: string; variantClientId?: string }
): Promise<AnalysisDocumentRow> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunDocuments(analysisId, runId)}`;
  return fetchJsonData<AnalysisDocumentRow>(url, {
    method: 'POST',
    body,
    requireAuth: true,
    errorMessage: 'Failed to generate document',
  });
}

/**
 * Generates the full standard set for a run:
 *   - Always: Analysis Summary (PDF)
 *   - When any DQI is over threshold: Disclosure Statement (DOCX), Shareholder Consent (DOCX),
 *     and one Waiver Agreement (DOCX) per over-threshold individual.
 */
export async function generateAllAnalysisDocuments(analysisId: string, runId: string): Promise<AnalysisDocumentRow[]> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisRunDocumentsGenerateAll(analysisId, runId)}`;
  return fetchJsonDataArray<AnalysisDocumentRow>(url, {
    method: 'POST',
    requireAuth: true,
    errorMessage: 'Failed to generate documents',
  });
}

/** Resolves the authenticated download URL for a Ready document. */
export function getAnalysisDocumentDownloadUrl(documentId: string): string {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.documentDownload(documentId)}`;
}

/**
 * Triggers an authenticated download in the browser. Uses fetch + Blob (not `<a download>`) so the
 * JWT in `Authorization` is honored — the backend requires bearer auth on the download endpoint.
 */
export async function downloadAnalysisDocument(doc: AnalysisDocumentRow): Promise<void> {
  if (doc.status !== 'Ready') {
    throw new Error('Document is not ready to download.');
  }
  const url = getAnalysisDocumentDownloadUrl(doc.id);
  const response = await authenticatedFetch(url, { requireAuth: true });
  if (!response.ok) {
    throw new Error(await messageFromFailedResponse(response, 'Failed to download document'));
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    const ext = doc.format === 'PDF' ? 'pdf' : doc.format === 'DOCX' ? 'docx' : 'bin';
    a.download = `${doc.title.replace(/[^\w\-]+/g, '_')}.v${doc.version}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  }
}

export async function getLastAnalysisRun(id: string): Promise<AnalysisLastRun | null> {
  return getLastAnalysisRunInBackend(id);
}

export async function getAnalysisRunHistory(id: string, limit = 50): Promise<AnalysisRunHistoryRow[]> {
  return getAnalysisRunHistoryInBackend(id, limit);
}

export async function getAnalysisRunSnapshot(
  analysisId: string,
  runId: string
): Promise<AnalysisRunSnapshotBody | null> {
  return getAnalysisRunSnapshotFromBackend(analysisId, runId);
}

class AnalysisApi {
  static getAnalyses = getAnalyses;
  static getAnalysesPage = getAnalysesPage;
  static getAnalysis = getAnalysis;
  static getArchivedAnalyses = getArchivedAnalyses;
  static getClientWorkspaceCompany = getClientWorkspaceCompany;
  static getLastAnalysisRun = getLastAnalysisRun;
  static getAnalysisRunHistory = getAnalysisRunHistory;
  static createAnalysis = createAnalysis;
  static updateAnalysis = updateAnalysis;
  static deleteAnalysis = deleteAnalysis;
  static restoreAnalysis = restoreAnalysis;
}

export default AnalysisApi;
